import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import config from "./config";
import { InlineLoader } from "./PageLoader";
import { addOutsidePress, removeOutsidePress } from "./outsidePress";

// "GIF" button + popover that searches GIPHY (trending by default) and calls
// onPick(gifUrl) with a hosted GIF URL. Dependency-free (uses fetch).
//
// The panel renders through a PORTAL with fixed positioning, so it is never
// clipped by an `overflow` ancestor (the Create Post modal, the lesson panel,
// the chat window). It picks above/below by available room and clamps to the
// viewport. `dropUp` is the *preferred* direction; it flips when space is tight.
const PANEL_W = 320;
const MARGIN = 8;

export default function GifPicker({ onPick, className = "", dropUp = true }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // { top, left }
  const [q, setQ] = useState("");
  const [gifs, setGifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const debounce = useRef(null);

  // Close on a click outside BOTH the button and the (portaled) panel.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    addOutsidePress(onDoc);
    return () => removeOutsidePress(onDoc);
  }, [open]);

  const place = useCallback(() => {
    const b = btnRef.current;
    if (!b) return;
    const r = b.getBoundingClientRect();
    const panelH = panelRef.current?.offsetHeight || 380;

    // Phones: a centred sheet spanning (almost) the full width, sitting just
    // ABOVE the composer (its bottom rests above the trigger button) so the send
    // input stays visible — avoids the cramped, edge-jammed 320px popover.
    if (window.innerWidth < 640) {
      const width = Math.min(window.innerWidth - MARGIN * 2, 460);
      const left = Math.round((window.innerWidth - width) / 2);
      const top = Math.max(MARGIN, r.top - panelH - 8);
      setPos({ top, left, width });
      return;
    }

    // Desktop: right-align the panel to the button, then keep it inside the viewport.
    let left = r.right - PANEL_W;
    left = Math.max(MARGIN, Math.min(left, window.innerWidth - PANEL_W - MARGIN));

    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    // Honour the preferred direction unless it doesn't fit and the other side does.
    const openUp = dropUp
      ? !(spaceAbove < panelH + MARGIN && spaceBelow > spaceAbove)
      : (spaceBelow < panelH + MARGIN && spaceAbove > spaceBelow);
    let top = openUp ? r.top - panelH - 8 : r.bottom + 8;
    top = Math.max(MARGIN, Math.min(top, window.innerHeight - panelH - MARGIN));

    setPos({ top, left, width: PANEL_W });
  }, [dropUp]);

  // Measure after the panel exists / its content changes, and keep it pinned
  // while the page scrolls or resizes.
  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place, gifs, loading]);

  const fetchGifs = (query) => {
    setLoading(true);
    const base = "https://api.giphy.com/v1/gifs";
    const url = query
      ? `${base}/search?api_key=${config.GIPHY_KEY}&q=${encodeURIComponent(query)}&limit=24&rating=pg-13&bundle=fixed_width`
      : `${base}/trending?api_key=${config.GIPHY_KEY}&limit=24&rating=pg-13&bundle=fixed_width`;
    fetch(url)
      .then((r) => r.json())
      .then((d) => {
        setGifs(
          (d.data || [])
            .map((g) => ({
              id: g.id,
              preview: g.images?.fixed_width?.url || g.images?.downsized?.url,
              full: g.images?.downsized_medium?.url || g.images?.fixed_width?.url,
            }))
            .filter((g) => g.preview && g.full)
        );
      })
      .catch(() => setGifs([]))
      .finally(() => setLoading(false));
  };

  // Load trending whenever the picker opens.
  useEffect(() => {
    if (open) fetchGifs(q.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSearch = (val) => {
    setQ(val);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchGifs(val.trim()), 350);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Add gif"
        className="flex items-center justify-center text-xs font-bold tracking-wide text-gray-500 hover:text-gray-700 transition"
      >
        GIF
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: "fixed",
              top: pos ? pos.top : -9999,
              left: pos ? pos.left : -9999,
              width: pos ? pos.width : PANEL_W,
              visibility: pos ? "visible" : "hidden",
            }}
            className="z-[10000] overflow-hidden rounded-2xl border border-gray-200 bg-surface shadow-2xl"
          >
            {/* Search */}
            <div className="p-2.5 border-b border-gray-100">
              <div className="relative">
                <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.3-4.3M11 18a7 7 0 100-14 7 7 0 000 14z" />
                </svg>
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => onSearch(e.target.value)}
                  placeholder="Search GIFs"
                  className="w-full text-sm bg-gray-100 rounded-lg pl-9 pr-3 py-2 outline-none focus:bg-gray-50 placeholder:text-gray-400"
                />
              </div>
            </div>

            {/* Results (2-column masonry) */}
            <div className="max-h-72 overflow-y-auto p-2">
              {loading ? (
                <InlineLoader className="justify-center py-12" />
              ) : gifs.length === 0 ? (
                <p className="py-12 text-center text-sm text-gray-400">No GIFs found</p>
              ) : (
                <div className="columns-2 gap-2">
                  {gifs.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => {
                        onPick(g.full);
                        setOpen(false);
                      }}
                      className="mb-2 block w-full overflow-hidden rounded-lg bg-gray-100 hover:opacity-90 transition"
                    >
                      <img src={g.preview} alt="" loading="lazy" className="w-full" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
